import React, { useContext } from 'react'
import { useAppSelector } from '../../../store/hooks'
import { useParams } from 'react-router-dom'
import classNames from 'classnames'
import {
  selectCrossClientResultExecutionLoading,
  selectCrossClientResultsExecution,
  selectCrossClientSelectedResultIds,
} from '../../../store/crossClient/crossClientResults'
import { CrmActionHostProvider } from '../Actions/CrmActionHostProvider'
import { CrossClientResultsInitialization } from './CrossClientResultsInitialization'
import { CrossClientResultsPoller } from './CrossClientResultsPoller'
import { CrossClientResultsViewReporter } from './CrossClientResultsViewReporter'
import { CrossClientActionsPoller } from '../Actions/CrossClientActionsPoller'
import { CrossClientQueryPhase, CrossClientQueryPhases } from '../../../models/CrossClient'
import { CrossClientTable } from '../Table/CrossClientTable'
import { CrossClientResultTabs } from './Filter/CrossClientResultTabs'
import { CrossClientNameFilter } from './Filter/CrossClientNameFilter'
import { CrossClientActionsFilter } from './Filter/CrossClientActionsFilter'
import { CrossClientResultBanner } from './CrossClientResultBanner'
import { createPortal } from 'react-dom'
import { CrossClientHeader } from './Header/CrossClientHeader'
import { CrossClientResultsHeader } from './Header/CrossClientResultsHeader'
import { CrossClientProgressPopover } from './Progress/CrossClientProgressPopover'
import { HeaderContext } from '../../LayoutWithLeftSidebar/HeaderContext'
import { Spinner } from '../../../components/Spinner/Spinner'
import { CrossClientQueryNotOwned } from '../NotOwned/CrossClientQueryNotOwned'
import './CrossClientResults.scss'

export const CrossClientResults = () => {
  const { executionId } = useParams()

  if (!executionId) {
    return <div>No query provided</div>
  }

  return (
    <>
      <CrossClientResultsInitialization executionId={executionId} />
      <CrossClientResultsPoller executionId={executionId} />
      <CrossClientResultsViewReporter executionId={executionId} />
      <CrossClientActionsPoller executionId={executionId} />
      <CrossClientResultsBody executionId={executionId} />
    </>
  )
}

interface CrossClientResultsBodyProps {
  executionId: string
}

const EVALUATION_PHASES: CrossClientQueryPhase[] = [
  CrossClientQueryPhases.INITIALIZED,
  CrossClientQueryPhases.COMPLETED,
  CrossClientQueryPhases.CANCELED,
  CrossClientQueryPhases.ERRORED,
]

const CrossClientResultsBody: React.FC<CrossClientResultsBodyProps> = () => {
  const { portalRef } = useContext(HeaderContext)

  const execution = useAppSelector(selectCrossClientResultsExecution)
  const executionLoading = useAppSelector(selectCrossClientResultExecutionLoading)
  const hasSelection = useAppSelector(selectCrossClientSelectedResultIds).length > 0

  if (executionLoading) {
    return (
      <div className='d-flex flex-column align-items-center justify-content-center gap-3 mt-10'>
        <Spinner />
        <div className='paragraph-medium medium'>Pulling up your results…</div>
      </div>
    )
  }

  if (!execution) {
    return <CrossClientQueryNotOwned />
  }

  if (!EVALUATION_PHASES.includes(execution.phase)) {
    return <div>Query is not in evaluation phase</div>
  }

  return (
    <CrmActionHostProvider>
      <div className='truly-full-size-content d-flex cross-client-results-container'>
        {/* eslint-disable-next-line react-hooks/refs -- pre-existing app-wide HeaderContext pattern: portal target ref is read during render */}
        {portalRef && portalRef.current ? createPortal(<CrossClientHeader executionId={execution.id} />, portalRef.current) : null}
        <div className='d-flex flex-column cross-client-results-left zocks-p-2.0 gap-4'>
          <div className='d-flex align-items-start justify-content-between gap-3'>
            <CrossClientResultsHeader execution={execution} />
            <CrossClientProgressPopover />
          </div>
          <div className='d-flex align-items-center justify-content-between gap-3'>
            <CrossClientResultTabs />
            <div className='d-flex align-items-center gap-2'>
              <CrossClientActionsFilter />
              <CrossClientNameFilter />
            </div>
          </div>
          <div className='cross-client-results-body'>
            <div className={classNames('cross-client-info-banner', { 'cross-client-info-banner--collapsed': hasSelection })} aria-hidden={hasSelection}>
              <div className='cross-client-info-banner__inner'>
                <CrossClientResultBanner />
              </div>
            </div>
            <CrossClientTable />
          </div>
        </div>
      </div>
    </CrmActionHostProvider>
  )
}
